import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Linkedin, Twitter, Facebook, Mail, Link as LinkIcon, Check, Share2 } from "lucide-react";
import { buildShareIntent, absoluteUrl } from "@/lib/social";
import { toast } from "sonner";

interface ShareBarProps {
  path: string;
  title: string;
  text?: string;
  variant?: "inline" | "compact";
  className?: string;
}

export default function ShareBar({ path, title, text, variant = "inline", className = "" }: ShareBarProps) {
  const url = absoluteUrl(path);
  const [copied, setCopied] = useState(false);

  const open = (target: "linkedin" | "x" | "facebook" | "email") => {
    const href = buildShareIntent(target, { url, title, text });
    if (target === "email") {
      window.location.href = href;
    } else {
      window.open(href, "_blank", "noopener,noreferrer,width=720,height=640");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const native = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: text ?? title, url });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const compact = variant === "compact";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {!compact && (
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mr-1">
          Share
        </span>
      )}
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={() => open("linkedin")}
        aria-label="Share on LinkedIn"
        className="gap-2"
      >
        <Linkedin className="h-4 w-4" />
        {!compact && "LinkedIn"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={() => open("x")}
        aria-label="Share on X"
        className="gap-2"
      >
        <Twitter className="h-4 w-4" />
        {!compact && "X"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={() => open("facebook")}
        aria-label="Share on Facebook"
        className="gap-2"
      >
        <Facebook className="h-4 w-4" />
        {!compact && "Facebook"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={() => open("email")}
        aria-label="Share via email"
        className="gap-2"
      >
        <Mail className="h-4 w-4" />
        {!compact && "Email"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={copy}
        aria-label="Copy link"
        className="gap-2"
      >
        {copied ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
        {!compact && (copied ? "Copied" : "Copy link")}
      </Button>
      {typeof navigator !== "undefined" && "share" in navigator && (
        <Button
          type="button"
          variant="outline"
          size={compact ? "icon" : "sm"}
          onClick={native}
          aria-label="Share"
          className="gap-2 sm:hidden"
        >
          <Share2 className="h-4 w-4" />
          {!compact && "Share"}
        </Button>
      )}
    </div>
  );
}